import { useDispatch } from "react-redux";
import { LeadDto } from "../interfaces/lead-dto.interface";
import { ApiError } from "../enums/api-error.enum";
import { LeadAnswer } from '../interfaces/lead-answer.interface';
import { displaySendLoader } from "../redux/display-slice";
import { receiveLead, setAppointmentDateSent } from "../redux/quiz-slice";
import { logService } from "../services/log.service";
import { analyticsService } from '../analytics.service';
import { AnalyticsEvent } from "../enums/analytics-event.enum";
import { useCreateLeadMutation, useUpdateLeadMutation } from "../services/api.service";
import { QuizEvent } from "../enums/quiz-event.enum";
import { postMessageService } from "../services/post-message.service";
import { useCallback, useContext } from "react";
import { ErrorMessageContext } from "../components/root/error-message.context";
import { useAppSelector } from "./redux.hook";
import { useFingerprint } from "./use-fingerprint.hook";

interface FormDto extends Record<string, any> {
  phone: string;
  extra?: Record<string, any>
}

export const useCreateLead = () => {
  const { results, distributorSelectedAnswer } = useAppSelector(state => state.quiz);
  const { quizId, leadExtraParams, extra: extraParams, test } = useAppSelector(state => state.service);
  const { bonuses } = useAppSelector(state => state.content.currentContent);
  const { distributorSelectedAnswerName } = useAppSelector(state => state.settings);
  const { setErrorMessage } = useContext(ErrorMessageContext);
  const [createLead] = useCreateLeadMutation();
  const dispatch = useDispatch();
  const { fingerprint } = useFingerprint();

  const sendLead = useCallback(async ({ phone, extra = {} }: FormDto) => {
    dispatch(displaySendLoader(true));
    let answers: LeadAnswer[] = [];
    let quiz_results: string;
    let results_params: Record<string, any> = {};

    if (results?.length) {
      results.forEach(result => {
        const answer = result.answer instanceof Array ? result.answer.join(', ') : result.answer;
        answers.push({ question: result.questionHeader, answer });
        results_params[result.questionName] = result.answerData;
      });

      quiz_results = answers.map(item => `${item.question.replace(/\?/g, "")}: ${item.answer}`).join('; ');
    } else quiz_results = "Без прохождения теста";

    const leadDto: LeadDto = {
      quiz: quizId,
      visitId: extraParams.visitId,
      phone,
      quiz_results,
      answers,
      results_params,
      test,
      extra: {
        [distributorSelectedAnswerName]: distributorSelectedAnswer?.value,
        distributorAnswerId: distributorSelectedAnswer?.id,
        bonuses: (bonuses?.map(b => b.title) || []).join(", "),
        ...leadExtraParams,
        ...extra,
        fingerprint
      },
    };

    setTimeout(() => {
      dispatch(displaySendLoader(false));
    }, 2600);

    try {
      const lead = await createLead(leadDto).unwrap();
      console.log("lead:", lead);

      if ((lead as any)?.message?.includes("spam")) {
        analyticsService.sendParams({ spam: 1 });
      }

      dispatch(receiveLead(lead));
      analyticsService.send(AnalyticsEvent.GET_LEAD, { lead });
      postMessageService.send({ event: QuizEvent.GET_LEAD, data: lead });

      return lead.id;

    } catch (error) {
      console.error(error);
      dispatch(displaySendLoader(false));
      logService.error(error?.error ? error.error : error);

      return new Promise<string>(resolve => {
        setErrorMessage({
          active: true,
          message: ApiError.SEND_LEAD_ERROR,
          onSubmit: () => {
            setErrorMessage({ active: false });
            resolve(sendLead({ phone, extra }));
          }
        });
      });
    }
  }, [test, fingerprint, results, distributorSelectedAnswer, quizId, leadExtraParams, distributorSelectedAnswerName, createLead, setErrorMessage, dispatch, extraParams.visitId]);

  return sendLead;
}

export const useUpdateLead = () => {
  const [patchLead] = useUpdateLeadMutation();
  const { receivedLead } = useAppSelector(state => state.quiz);
  const dispatch = useDispatch();
  const { setErrorMessage } = useContext(ErrorMessageContext);

  const updateLead = async (updateLeadDto: Partial<LeadDto>) => {
    dispatch(displaySendLoader(true));
    if (!receivedLead) return;

    setTimeout(() => {
      dispatch(displaySendLoader(false));
      dispatch(setAppointmentDateSent());
    }, 2600);

    try {
      const lead = await patchLead({ id: receivedLead.id, body: updateLeadDto }).unwrap();
      return lead.id;

    } catch (error) {
      dispatch(displaySendLoader(false));
      console.error(error);
      logService.error(error);

      return new Promise<string>(resolve => {
        setErrorMessage({
          active: true,
          message: ApiError.SEND_LEAD_ERROR,
          onSubmit: () => {
            setErrorMessage({ active: false });
            resolve(updateLead(updateLeadDto));
          }
        });
      });
    }
  }

  return updateLead;
}